import { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch } from '../utils/streamApi';
import AttendanceLiveView from '../components/AttendanceLiveView';

// My Attendance — employee self-view. Pick your name, see today's status, and tap
// Mark Attendance to open the checkpoint's live view; it flips to ✓ Marked the
// instant the engine records your check-in (drives off the real /ui/attendance/my).

const fmtTime = ts => { try { return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };

export default function MyAttendancePage() {
  const [employees, setEmployees] = useState([]);
  const [name, setName]   = useState(() => localStorage.getItem('prvsk_att_me') || '');
  const [st, setSt]       = useState(null);
  const [marking, setMarking] = useState(false);   // false or a checkpoint object
  const [cpIdx, setCpIdx] = useState(0);
  const prevCheckin = useRef(null);
  const [justMarked, setJustMarked] = useState(false);

  useEffect(() => { apiFetch('/ui/attendance/form-options').then(r => r.ok ? r.json() : null).then(d => d && setEmployees(d.employees || [])); }, []);

  const load = useCallback(async () => {
    if (!name) { setSt(null); return; }
    const r = await apiFetch(`/ui/attendance/my?name=${encodeURIComponent(name)}`);
    if (r.ok) {
      const d = await r.json();
      // detect a fresh check-in while marking → celebrate
      if (marking && d.checkin && !prevCheckin.current) setJustMarked(true);
      prevCheckin.current = d.checkin;
      setSt(d);
    }
  }, [name, marking]);

  useEffect(() => { prevCheckin.current = null; if (name) localStorage.setItem('prvsk_att_me', name); load(); }, [name]); // eslint-disable-line
  useEffect(() => { const t = setInterval(load, marking ? 2500 : 15000); return () => clearInterval(t); }, [load, marking]);

  const startMark = () => { setJustMarked(false); prevCheckin.current = st?.checkin || null; setMarking(true); };
  const stopMark  = () => { setMarking(false); setJustMarked(false); };

  const requestCorrection = async () => {
    const msg = window.prompt('Describe the correction (reason + the time you want):');
    if (!msg) return;
    const event_id = st?.checkout?.id || st?.checkin?.id || null;   // link to the mark being corrected, if any
    const r = await apiFetch('/ui/attendance/corrections', { method: 'POST', json: { name, message: msg, event_id } });
    window.alert(r.ok ? 'Correction request submitted for review.' : 'Could not submit request.');
  };

  const cps = st?.checkpoints || [];
  const cp = cps[Math.min(cpIdx, cps.length - 1)] || null;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🕘 My Attendance</h1>
      <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{st?.date || new Date().toISOString().slice(0, 10)}</div>

      <div style={card}>
        <div style={label}>I am</div>
        <select value={name} onChange={e => setName(e.target.value)} style={{ ...input, minWidth: 220 }}>
          <option value="">Select your name…</option>
          {employees.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {name && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <StatusTile title="Check-In"  ev={st?.checkin} />
            <StatusTile title="Check-Out" ev={st?.checkout} />
          </div>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            {st?.late && <span style={{ color: 'var(--warning)', fontWeight: 600 }}>⚠ Late check-in</span>}
            {st?.hours != null && <span style={{ color: 'var(--text-muted)' }}>Hours today: <b style={{ color: 'var(--text)' }}>{st.hours}</b></span>}
            <button onClick={requestCorrection} style={{ ...btn, marginLeft: 'auto' }}>Request correction</button>
          </div>

          {/* Without this the screen looks completely normal and simply never
              marks them — the single most confusing state in the module. */}
          {st && st.onboarded === false && (
            <div style={{ ...card, borderColor: 'var(--danger)' }}>
              ⚠ You have not been onboarded yet, so you cannot be marked automatically.
              Ask an admin to enrol your face (Attendance Setup → Onboarding), or raise a hand
              at a gate to send an onboarding request for approval.
            </div>
          )}

          {st?.pending_count > 0 && !st?.checkin && (
            <div style={{ ...card, borderColor: 'var(--warning)' }}>
              ⏳ An attendance attempt was recorded but is <b>pending verification</b>. It will appear once an admin confirms your identity.
            </div>
          )}

          {!marking ? (
            <button onClick={startMark} disabled={!cps.length} style={{ ...primaryBtn, marginTop: 16, fontSize: 16, padding: '12px 20px' }}>
              {cps.length ? 'MARK ATTENDANCE' : 'No attendance point configured'}
            </button>
          ) : (
            <div style={{ ...card, marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <b>Stand inside the zone to be marked</b>
                <button onClick={stopMark} style={btn}>Done</button>
              </div>
              {cps.length > 1 && (
                <div style={{ margin: '10px 0' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 13, marginRight: 8 }}>I'm at:</span>
                  <select value={cpIdx} onChange={e => setCpIdx(Number(e.target.value))} style={input}>
                    {cps.map((c, i) => <option key={c.id} value={i}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                {cp && <AttendanceLiveView cameraIdx={cp.camera_idx} checkpointId={cp.id}
                         polygon={(cp.zones && cp.zones[0]) || []} marked={justMarked}
                         holdSeconds={cp.gesture_hold_seconds || 1.5} />}
              </div>
              {/* Only surfaced once recognition has actually failed. Telling
                  everyone to raise a hand up front would make a gesture out of a
                  walk-through and fill the review queue with people who were
                  recognised perfectly well. */}
              {cp?.exception_on_gesture !== false && !justMarked &&
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
                  Not recognised? Raise a hand — the attempt is sent for review, it does not mark you present.
                </div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusTile({ title, ev }) {
  const marked = !!ev;
  return (
    <div style={{ ...card, flex: '1 1 200px', marginTop: 0, borderColor: marked ? 'var(--accent)' : 'var(--border)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{marked ? fmtTime(ev.ts) : 'Not Marked'}</div>
      {marked && ev.identity_status && ev.identity_status !== 'VERIFIED' &&
        <div style={{ color: 'var(--warning)', fontSize: 12, marginTop: 2 }}>{ev.identity_status.replace(/_/g, ' ').toLowerCase()}</div>}
    </div>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const label = { fontSize: 13, fontWeight: 600, marginBottom: 8 };
const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn = { padding: '7px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const primaryBtn = { ...btn, background: 'var(--primary)', color: 'var(--primary-light)', border: 'none', fontWeight: 700 };
